// Console Glass — AntD token sets for the two palettes (task 355.1; reads palette.ts since 355.3).
//
//   Night glass (dark): abyss ground, translucent teal-tinted glass surfaces, neon teal accent.
//   Day frost  (light): frosted white glass on a pale teal ground; the navigation panel stays dark
//                       slate (Menu theme="dark" + the NAV_MENU dark* tokens below, since 355.4).
//
// Colours come from palette.ts (the single source); what AntD cannot express as a token (backdrop
// blur, the ground, glows) lives in src/styles/glass.css via the generated `--cg-*` variables.

import type { ThemeConfig } from 'antd';
import { DAY_PALETTE, FONT_MONO, FONT_SANS, NIGHT_PALETTE } from './palette';

export { FONT_MONO, FONT_SANS };

type GlassTheme = Pick<ThemeConfig, 'token' | 'components'>;

const SHARED_TOKEN: ThemeConfig['token'] = {
  fontFamily: FONT_SANS,
  fontFamilyCode: FONT_MONO,
  borderRadius: 10,
  borderRadiusLG: 14,
  borderRadiusSM: 7,
  borderRadiusXS: 4,
  // The ground (gradient + arc) is painted on <body> by glass.css; layouts must let it through.
  colorBgLayout: 'transparent',
};

const N = NIGHT_PALETTE;
const D = DAY_PALETTE;

// The shell's navigation panel is dark slate in BOTH palettes (task 355.4) and renders the AntD Menu with
// theme="dark"; these dark* tokens style it identically in Night glass and Day frost. Other menus (e.g. the
// Workflows category list) use the default light theme and are unaffected by the dark* tokens.
const NAV_MENU: NonNullable<ThemeConfig['components']>['Menu'] = {
  darkItemBg: 'transparent',
  darkSubMenuItemBg: 'transparent',
  darkPopupBg: 'rgba(15, 29, 39, 0.97)',
  darkItemColor: N.navInk,
  darkItemHoverColor: '#ffffff',
  darkItemHoverBg: 'rgba(255, 255, 255, 0.06)',
  darkItemSelectedColor: '#ffffff',
  darkItemSelectedBg: 'rgba(34, 211, 200, 0.16)',
  darkGroupTitleColor: N.navInk2,
  itemHeight: 36,
  itemMarginInline: 0,
  itemMarginBlock: 2,
  itemBorderRadius: 10,
  iconSize: 17,
  collapsedIconSize: 18,
  collapsedWidth: 54,
};

export const NIGHT_GLASS: GlassTheme = {
  token: {
    ...SHARED_TOKEN,
    colorPrimary: '#1fcfc5',
    colorSuccess: N.mint,
    colorWarning: N.amber,
    colorError: N.rose,
    colorLink: '#4fe3d8',
    colorLinkHover: '#8af3ea',
    colorLinkActive: '#1fcfc5',
    colorBgBase: '#070f16',
    colorTextBase: N.ink,
    colorBgContainer: N.glass,
    colorBgElevated: 'rgba(15, 29, 39, 0.96)',
    colorBorder: N.fieldEdge,
    colorBorderSecondary: 'rgba(140, 235, 228, 0.1)',
    colorSplit: N.line,
    colorFillAlter: 'rgba(255, 255, 255, 0.03)',
  },
  components: {
    Menu: {
      ...NAV_MENU,
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      popupBg: 'rgba(15, 29, 39, 0.96)',
      itemColor: N.navInk,
      itemHoverColor: N.navInkHover,
      itemHoverBg: 'rgba(255, 255, 255, 0.05)',
      itemSelectedColor: N.navSelected,
      itemSelectedBg: 'rgba(0, 190, 185, 0.16)',
      itemActiveBg: 'rgba(0, 190, 185, 0.2)',
      activeBarBorderWidth: 0,
    },
    Card: { headerBg: 'transparent', colorBorderSecondary: 'rgba(140, 235, 228, 0.13)' },
    Table: {
      // Tables sit inside glass cards: a second translucent layer would double the tint.
      colorBgContainer: 'transparent',
      // Task 487: denser cells than AntD's 16 / 12 / 8 px — compact (small) is the default density.
      cellPaddingBlock: 12,
      cellPaddingBlockMD: 8,
      cellPaddingBlockSM: 4,
      cellPaddingInlineSM: 8,
      headerBg: 'rgba(255, 255, 255, 0.03)',
      headerSplitColor: N.line,
      borderColor: N.line,
      rowHoverBg: 'rgba(0, 200, 190, 0.06)',
      rowSelectedBg: 'rgba(0, 200, 190, 0.1)',
      rowSelectedHoverBg: 'rgba(0, 200, 190, 0.14)',
      footerBg: 'transparent',
      // AntD derives the sorted-column colours by blending onto colorBgContainer; on 'transparent' that comes out
      // black (task 477: a default-sorted column rendered as a black block). Set them explicitly.
      bodySortBg: 'rgba(255, 255, 255, 0.02)',
      headerSortActiveBg: 'rgba(255, 255, 255, 0.06)',
      headerSortHoverBg: 'rgba(255, 255, 255, 0.05)',
    },
    Button: {
      primaryColor: '#021514',
      primaryShadow: '0 0 18px -2px rgba(20, 220, 210, 0.55)',
      defaultBg: 'rgba(255, 255, 255, 0.05)',
      defaultBorderColor: N.fieldEdge,
      defaultShadow: 'none',
      dangerShadow: 'none',
    },
    Modal: { contentBg: 'rgba(14, 26, 35, 0.9)', headerBg: 'transparent', footerBg: 'transparent' },
    Progress: { remainingColor: N.line },
    Tooltip: { colorBgSpotlight: 'rgba(15, 29, 39, 0.96)' },
    Select: { optionSelectedBg: 'rgba(0, 190, 185, 0.18)' },
  },
};

export const DAY_FROST: GlassTheme = {
  token: {
    ...SHARED_TOKEN,
    colorPrimary: '#00aaaa',
    colorLink: '#007f86',
    colorLinkHover: '#005f64',
    colorLinkActive: '#00aaaa',
    colorTextBase: '#14212b',
    colorBgContainer: D.glass,
    colorBgElevated: 'rgba(255, 255, 255, 0.97)',
    colorBorder: D.rule,
    colorBorderSecondary: D.line,
    colorSplit: 'rgba(19, 32, 41, 0.07)',
    colorFillAlter: 'rgba(19, 32, 41, 0.025)',
  },
  components: {
    Menu: {
      ...NAV_MENU,
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      activeBarBorderWidth: 0,
    },
    Card: { headerBg: 'transparent', colorBorderSecondary: 'rgba(255, 255, 255, 0.9)' },
    Table: {
      colorBgContainer: 'transparent',
      // Task 487: denser cells than AntD's 16 / 12 / 8 px — compact (small) is the default density.
      cellPaddingBlock: 12,
      cellPaddingBlockMD: 8,
      cellPaddingBlockSM: 4,
      cellPaddingInlineSM: 8,
      headerBg: 'rgba(19, 32, 41, 0.03)',
      rowHoverBg: 'rgba(0, 154, 154, 0.05)',
      rowSelectedBg: 'rgba(0, 170, 170, 0.08)',
      rowSelectedHoverBg: 'rgba(0, 170, 170, 0.12)',
      footerBg: 'transparent',
      // See the dark theme: explicit, as the derived values blend onto 'transparent' and come out black.
      bodySortBg: 'rgba(19, 32, 41, 0.02)',
      headerSortActiveBg: 'rgba(19, 32, 41, 0.06)',
      headerSortHoverBg: 'rgba(19, 32, 41, 0.05)',
    },
    Button: {
      primaryShadow: '0 6px 16px -6px rgba(0, 140, 140, 0.6)',
      defaultBg: 'rgba(255, 255, 255, 0.75)',
      defaultShadow: 'none',
      dangerShadow: 'none',
    },
    Modal: {
      contentBg: 'rgba(255, 255, 255, 0.9)',
      headerBg: 'transparent',
      footerBg: 'transparent',
    },
  },
};
